import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ListChecks, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg } from "@/lib/api";

const EMPTY = { title: "", description: "", subject: "", categories: [], duration_minutes: 30, pass_percentage: 40, published: true };

const TestForm = ({ open, onOpenChange, initial, onSaved, allCats }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ ...EMPTY, ...initial, categories: initial?.categories || [] }); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const toggleCat = (name) => {
    setF((prev) => ({
      ...prev,
      categories: prev.categories.includes(name) ? prev.categories.filter((c) => c !== name) : [...prev.categories, name],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, duration_minutes: Number(f.duration_minutes), pass_percentage: Number(f.pass_percentage) };
      if (initial?.id) await api.put(`/admin/tests/${initial.id}`, body); else await api.post("/admin/tests", body);
      toast.success(initial?.id ? "Test updated" : "Test created");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="test-form-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit test" : "New test"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Title</Label><Input required value={f.title} onChange={set("title")} data-testid="admin-test-title-input" /></div>
          <div className="space-y-1.5"><Label>Subject</Label><Input value={f.subject} onChange={set("subject")} placeholder="e.g. Mathematics" data-testid="admin-test-subject-input" /></div>
          <div className="space-y-1.5">
            <Label>Categories</Label>
            <div className="text-xs text-slate-500 -mt-1">Tick the exam streams this test is for. Leave blank to make it visible to everyone.</div>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="admin-test-categories-picker">
              {allCats.length === 0 && <span className="text-xs text-slate-400">No approved categories yet.</span>}
              {allCats.map((c) => {
                const on = f.categories.includes(c.name);
                return (
                  <button key={c.id} type="button" onClick={() => toggleCat(c.name)} data-testid={`admin-test-category-chip-${c.name}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      on ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-700 border-slate-200 hover:border-[#2563EB]"
                    }`}>
                    {c.name}{on && <X className="h-3 w-3 ml-1 inline" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={f.description} onChange={set("description")} data-testid="admin-test-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Duration (minutes)</Label><Input type="number" min={1} max={600} required value={f.duration_minutes} onChange={set("duration_minutes")} data-testid="admin-test-duration-input" /></div>
            <div className="space-y-1.5"><Label>Pass percentage</Label><Input type="number" min={0} max={100} required value={f.pass_percentage} onChange={set("pass_percentage")} data-testid="admin-test-pass-input" /></div>
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
            <div><div className="text-sm font-medium">Published</div><div className="text-xs text-slate-500">Visible to students</div></div>
            <Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="admin-test-published-switch" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="admin-save-test-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">Save test</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminTests() {
  const nav = useNavigate();
  const [tests, setTests] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => Promise.all([api.get("/admin/tests"), api.get("/admin/categories")])
    .then(([t, c]) => { setTests(t.data); setCats(c.data.filter((x) => x.status === "approved")); })
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    try { await api.delete(`/admin/tests/${t.id}`); toast.success("Test deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><div className="small-label">Control Panel</div><h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Tests</h1></div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-create-test-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
          <Plus className="h-4 w-4 mr-1.5" />New test
        </Button>
      </div>
      <div className="mt-8 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="admin-tests-table">
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Categories</TableHead><TableHead>Duration</TableHead>
            <TableHead>Questions</TableHead><TableHead>Pass</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {tests.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No tests yet. Create your first one.</TableCell></TableRow>}
            {tests.map((t) => (
              <TableRow key={t.id} data-testid={`admin-test-row-${t.id}`}>
                <TableCell className="font-medium">{t.title}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(t.categories && t.categories.length > 0) ? t.categories.map((c) => (
                      <Badge key={c} variant="outline" className="font-mono text-[10px] uppercase">{c}</Badge>
                    )) : <span className="text-xs text-slate-400">Open to all</span>}
                  </div>
                </TableCell>
                <TableCell className="font-mono">{t.duration_minutes}m</TableCell>
                <TableCell className="font-mono">{t.question_count}</TableCell>
                <TableCell className="font-mono">{t.pass_percentage}%</TableCell>
                <TableCell>{t.published ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Published</Badge> : <Badge variant="outline">Draft</Badge>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => nav(`/admin/tests/${t.id}`)} data-testid={`admin-manage-questions-${t.id}`} className="hover:bg-slate-900 hover:text-white mr-1">
                    <ListChecks className="h-4 w-4 mr-1" />Questions
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`admin-edit-test-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete testId={`admin-delete-test-${t.id}`} title="Delete test?" description="This deletes the test, all its questions and all student results for it."
                    onConfirm={() => remove(t)}
                    trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-test-${t.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TestForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} allCats={cats} />
    </div>
  );
}
